/**
 * Hungarian message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal te (locked in l10n/locales.md).
 *
 * Platform terms follow Hungarian UI conventions (Téma for theme,
 * beszélgetés for conversation, párosítás for pair, elvetés for dismiss,
 * fogadó eszköz for a receiving surface); "Glance" is the untranslated
 * feature brand name (glossary D1) and "klip" is a locked loanword (D2).
 *
 * Plural notes: Hungarian is a {one, other} CLDR locale, but after a
 * numeral the noun stays SINGULAR (5 eszköz, not 5 eszközök), so the two
 * grammatical forms are usually identical text. The `selectPlural("hu", …)`
 * shape is kept for parity with the en catalog.
 *
 * Placeholder rule: Hungarian case suffixes are never attached to a
 * substituted value (vowel harmony is unknowable), so `on %s` / `via %s`
 * style constructions are restructured with a colon or a head noun.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Csatlakoztatva",
    statusReconnecting: "Újracsatlakozás…",
    statusDisconnected: "Nincs kapcsolat",
    openSettings: "Beállítások megnyitása",
    pairedFallbackDevice: "a telefonod",
    publicModeToggleEnable: "Értesítések tartalmának elrejtése",
    publicModeToggleDisable: "Értesítések tartalmának megjelenítése",
  },

  notifications: {
    replyAriaLabel: "Válasz",
    replyPlaceholder: "Válasz…",
    replySend: "Küldés",
    sent: "Elküldve",
    failedToSend: "A küldés sikertelen",
    unread: "Olvasatlan",
    dismiss: "Elvetés",
    notificationImageAlt: "Értesítés képe",
  },

  conversations: {
    replyAriaLabel: "Válasz",
    replyPlaceholder: "Válasz…",
    replySend: "Küldés",
    sent: "Elküldve",
    failedToSend: "A küldés sikertelen",
    dismiss: "Beszélgetés elvetése",
    messagesListLabel: "Üzenetek ebben a beszélgetésben",
    deviceOwner: "Te",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Nincs olvasatlan üzenet"
        : `${count} ${selectPlural("hu", count, {
            one: "olvasatlan üzenet",
            other: "olvasatlan üzenet",
          })}`,
  },

  list: {
    today: "Ma",
    yesterday: "Tegnap",
    searchNotifications: "Értesítések keresése",
    clearSearch: "Keresés törlése",
    clearAll: "Összes törlése",
    markAllRead: "Összes olvasottnak jelölése",
    groupCheckboxLabel: "Csoportosítás",
    noNotificationsTitle: "Még nincsenek értesítések",
    noNotificationsDesc: "A telefonodról tükrözött új üzenetek itt jelennek meg.",
    noMatchesTitle: "Nincs találat",
    notificationsList: "Értesítések",
    privacyAppList: "Értesítések alkalmazásonként",
    clear: "Törlés",
    clearMatches: (count: number): string =>
      `${count} ${selectPlural("hu", count, { one: "találat", other: "találat" })} törlése`,
    noMatchesDesc: (query: string): string => `Nincs a keresésnek megfelelő értesítés: „${query}”.`,
    appNotificationsGroup: (appName: string): string => `${appName} értesítései`,
    dismissAllFrom: (appName: string): string => `Az összes elvetése innen: ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("hu", count, { one: "üzenet", other: "üzenet" })
          : kind === "calls"
            ? selectPlural("hu", count, { one: "hívás", other: "hívás" })
            : selectPlural("hu", count, { one: "értesítés", other: "értesítés" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "elvetés",
    kbdNavigate: "navigálás",
    kbdOrSeparator: "vagy",
  },

  settings: {
    back: "Vissza az értesítésekhez",
    settings: "Beállítások",
    general: "Általános",
    theme: "Téma",
    themeAuto: "Automatikus",
    themeLight: "Világos",
    themeDark: "Sötét",
    language: "Nyelv",
    languageSystemDefault: "Rendszer alapértelmezése",
    notificationsSection: "Értesítések",
    osNotifications: "Rendszerértesítések",
    callNotifications: "Hívásértesítések",
    privacySecurity: "Adatvédelem",
    analyticsToggle: "Névtelen használati statisztika",
    analyticsToggleDescription: "Csak számlálók és mérföldkövek — soha az értesítéseid.",
    statusConnected: "Csatlakoztatva",
    statusReconnecting: "Újracsatlakozás…",
    statusDisconnected: "Nincs kapcsolat",
    disconnectFromPhone: "Kapcsolat bontása a telefonnal",
    disconnectConfirm: "Biztos vagy benne? Ez megszünteti az eszközöd párosítását.",
    yesDisconnect: "Igen, bontás",
    cancel: "Mégse",
    version: "Verzió",
    encryption: "Titkosítás",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Segítségre van szükséged? Írj a __EMAIL__ címre",
    disconnectFailed: "A kapcsolat bontása sikertelen — próbáld újra",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `A kapcsolat bontva, de a helyi törlés sikertelen: ${errorMsg}. Töltsd újra az oldalt.`,
    disconnectLocalCleanupFailed:
      "A kapcsolat bontva, de a helyi törlés sikertelen. Töltsd újra az oldalt.",
  },

  pairing: {
    enterCodeInstruction: "Írd be ezt a kódot a Reflecto alkalmazásba",
    pairedSuccessfully: "Sikeres párosítás!",
    pairingFailed: "A párosítás sikertelen",
    requestingPairingCode: "Párosítási kód kérése…",
    codeExpired: "A kód lejárt",
    tryAgain: "Próbáld újra",
    failedToStartPairing: "A párosítás indítása sikertelen",
    installHeading: "Még nincs meg az alkalmazás?",
    installScanHint: "Olvasd be a telefonod kamerájával",
    installQrLabel: "QR-kód a Reflecto telefonra telepítéséhez",
    capabilitiesHeading: "Párosítás után:",
    capabilityMirror: "Itt látod a telefonod értesítéseit",
    capabilityReply: "Válaszolhatsz anélkül, hogy kézbe vennéd a telefont",
    capabilityShare: "Megoszthatod a vágólapot és fájlokat mindkét irányban",
    encryptionNote: "Végpontok között titkosítva. A kiszolgáló sosem látja a tartalmadat.",
  },

  dnd: {
    title: "Ne zavarjanak",
    description: "Szüneteltesd az értesítéseket ezen az eszközön a következő egy órára.",
    snooze1h: "Szüneteltetés 1 órára",
    snoozeActiveUntil: (formattedTime: string): string => `Aktív eddig: ${formattedTime}`,
    cancelSnooze: "Mégse",
  },

  connectors: {
    title: "Csatlakoztatott szolgáltatások",
    emptyBody:
      "Még nincsenek csatlakoztatott szolgáltatások. Csatlakoztasd a ChatGPT-t vagy a Claude-ot a csatlakozóbeállításaikból, majd hagyd jóvá a kódot a telefonodon.",
    scopeSend: "Küldhet értesítéseket",
    added: (date: string): string => `Hozzáadva ${date}`,
    lastUsed: (relative: string): string => `Utoljára használva ${relative}`,
    neverUsed: "Soha nem használt",
    revoke: "Kapcsolat bontása",
    revokeConfirm: (label: string): string =>
      `Bontod a(z) ${label} kapcsolatát? Azonnal elveszíti a hozzáférést.`,
    revokeAction: "Kapcsolat bontása",
    revokeConfirmLabel: (label: string): string => `A(z) ${label} kapcsolatának bontása`,
    cancel: "Mégse",
    loadError: "A csatlakoztatott szolgáltatásokat nem sikerült betölteni. Próbáld újra.",
    revokeError: "A szolgáltatás kapcsolatát nem sikerült bontani. Próbáld újra.",
    retry: "Újra",
  },

  safetyNumber: {
    rowLabel: "Titkosítás ellenőrzése",
    rowSublabel: "Hasonlítsa össze a biztonsági számokat a telefonjával",
    cardTitle: "Biztonsági szám",
    caption: (deviceLabel: string): string =>
      `Hasonlítsa össze a ${deviceLabel} eszközön megjelenő számmal. Ha mindkettő megegyezik, a végponttól végpontig terjedő titkosítás intakt.`,
    repairNote: "Ez a szám megváltozik, amikor újrapárosít egy eszközt.",
    copy: "Másolás",
    copied: "Másolva",
    maskedHint: "Rejtett, amíg az adatvédelmi mód be van kapcsolva",
  },

  feedback: {
    title: "Visszajelzés küldése",
    close: "Bezárás",
    messageLabel: "Az üzeneted",
    messagePlaceholder: "Hibát találtál, van egy ötleted, vagy valami nem stimmel? Írd meg nekünk.",
    categoryLabel: "Kategória",
    categoryBug: "Hiba",
    categoryIdea: "Ötlet",
    categoryOther: "Egyéb",
    emailLabel: "E-mail (nem kötelező, csak ha választ szeretnél)",
    emailPlaceholder: "te@pelda.hu",
    emailHelp: "Nem teszünk fel semmilyen listára.",
    disclosure:
      "Az üzeneted a Reflecto szerverére kerül, és rögzítjük a belső hibakövetőnkben. Csatoljuk az alkalmazásod verzióját és egy, a párosításkor kiadott eszközazonosítót, hogy összefüggésbe hozhassuk a jelentéseket. Nem látjuk az értesítéseidet, a névjegyeidet, sem más személyes adatot. Az e-mail megadása nem kötelező — csak akkor add meg, ha választ szeretnél. Nem teszünk fel semmilyen listára.",
    submit: "Visszajelzés küldése",
    sending: "Küldés…",
    success: "Köszönjük — ember olvassa, általában 24 órán belül",
    error: "A küldés sikertelen. Próbáld újra.",
    done: "Kész",
    sendFeedback: "Visszajelzés küldése",
  },

  uninstall: {
    headline: "A Reflecto bővítménye el van távolítva",
    body: "Fiók sosem volt, a bővítmény pedig ezen a számítógépen tartotta a párosításodat, így nincs mit eltakarítanod.",
    ask: "Ha van harminc másodperced, szívesen megtudnánk, mi ment félre.",
    categoryLegend: "Milyen visszajelzés ez?",
    categoryBug: "Valami elromlott",
    categoryIdea: "Hiányzott belőle valami, amire szükségem volt",
    categoryOther: "Valami más",
    messageLabel: "Mi ment félre?",
    messagePlaceholder: "Pár óra után nem érkezett több értesítés.",
    messageRequired: "Írj előbb egy mondatot, aztán küldd el.",
    charactersLeft: (count: number): string => `${count} karakter maradt`,
    emailLabel: "E-mail (nem kötelező)",
    emailPlaceholder: "te@pelda.hu",
    emailHelp: "Csak arra használjuk, hogy válaszoljunk. Semmi másra.",
    submit: "Visszajelzés küldése",
    sending: "Küldés…",
    retry: "Próbáld újra",
    privacyNote:
      "Ez az űrlap nincs egyetlen eszközhöz sem kötve. Nem tartalmazza az értesítéseidet, a vágólapodat és a fájljaidat sem. Az e-mail megadása nem kötelező, és csak a válaszhoz használjuk.",
    success: "Köszönjük. Mindegyiket ember olvassa el.",
    errorGeneric: "Nem sikerült elküldeni. Az üzeneted megmaradt.",
    errorRateLimited:
      "Mostanában több üzenetet is küldtél. Próbáld újra körülbelül egy óra múlva. A szöveged itt marad.",
    footerWebApp: "A Reflecto webalkalmazása a bővítmény nélkül is működik.",
    footerWebAppLink: "Webalkalmazás megnyitása",
  },

  glance: {
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Bezárás",
    aiSummaryTag: "AI-összefoglaló",
    errorAi: "Most nem sikerült összefoglalót készíteni.",
    retryCta: "Próbáld újra",
    downloadHeading: "Kérj AI-összefoglalókat",
    downloadBody:
      "Nézd meg egy eszközön futó AI-összegzésben, miről maradtál le. Teljes egészében az eszközödön fut.",
    downloadCta: "Modell letöltése",
    downloadPrivacyNote: "Egyszeri letöltés. A szerver soha nem látja az értesítéseidet.",
    downloadingHeading: "Modell letöltése…",
    caughtUp: "Mindennel naprakész vagy — most semmi sem igényli a figyelmedet.",
    needsYou: "Figyelmet igényel",
    quiet: "Egyéb",
    scanning: (pending: number): string => `${pending} elem összegzése…`,
    jump: "Értesítés megnyitása",
    affordanceReply: "Válasz",
    affordanceOpen: "Megnyitás",
    affordanceCopyOtp: "Kód másolása",
    buttonLabel: "Glance",
  },

  update: {
    title: "Frissítés érhető el",
    description: "A Reflecto új verziója készen áll. A frissítéshez töltsd újra.",
    reload: "Újratöltés",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Ezt az eszközt így neveztük el: „${name}”.`,
    cardLooksGood: "Jó lesz",
    cardEdit: "Szerkesztés",
    cardAriaLabel: "Eszköz nevének megerősítése",
    renameTitle: "Eszköz átnevezése",
    renameLabel: "Eszköz neve",
    renameSave: "Mentés",
    renameCancel: "Mégse",
    renameFailed: "Az átnevezés sikertelen. Próbáld újra.",
  },

  collision: {
    title: (name: string): string => `Már van egy „${name}” nevű eszközöd`,
    subtitle: "Minek nevezzük ezt?",
    chipWork: (name: string): string => `Munka ${name}`,
    chipHome: (name: string): string => `Otthon ${name}`,
    chipPersonal: (name: string): string => `Saját ${name}`,
    customLabel: "Egyéni név",
    save: "Mentés",
    useAnyway: "Mégis az eredeti nevet használom",
    saveFailed: "Az új nevet nem sikerült elmenteni. Próbáld újra.",
    ariaLabel: "Ismétlődő eszköznév feloldása",
  },

  clipboard: {
    title: "Vágólap",
    emptyTitle: "Még nincsenek klipek",
    empty: "A többi eszközödről érkező klipek itt jelennek meg. Koppints a másoláshoz.",
    from: (label: string): string => `innen: ${label}`,
    to: (label: string): string => `címzett: ${label}`,
    copy: "Másolás a vágólapra",
    copied: "Másolva",
    reveal: "Felfedés",
    hide: "Elrejtés",
    maskedHint: "A tartalom rejtve — a megjelenítéshez nyomd meg a Felfedés gombot",
    sensitiveChip: "Érzékeny",
    cloudChip: "az API-tokenedből",
    delete: "Klip törlése",
    sendPlaceholder: "Illeszd be a küldendő szöveget…",
    sendCta: "Küldés",
    sendSensitiveToggle: "Megjelölés érzékenyként",
    sendSuccess: (n: number): string =>
      `Elküldve ${n} ${selectPlural("hu", n, { one: "eszközre", other: "eszközre" })}.`,
    sendPartial: (n: number, total: number): string =>
      `${n}/${total} ${selectPlural("hu", total, { one: "eszköz", other: "eszköz" })} elérve.`,
    sendNoDevices: "Nincs párosított eszköz, amelyre küldeni lehetne.",
    sendFailed: "A küldés sikertelen. Próbáld újra.",
    sendEmpty: "Nincs mit küldeni.",
    dockRecentHeading: "Legutóbbi",
    dockEmptyClipboard: "A vágólapod üres.",
    clearAll: "Összes törlése",
    clearAllConfirm: "Igen, összes törlése",
    clearAllCancel: "Mégse",
    shareTitle: "Küldés az eszközeidre",
    shareReview:
      "Ellenőrizd, mit küldesz. A párosított eszközeidre kerül, végpontok között titkosítva.",
    shareSend: "Küldés",
    shareCancel: "Mégse",
    shareNotPaired: "A megosztott szöveg küldéséhez előbb párosíts egy eszközt.",
    shareOpenApp: "Reflecto megnyitása",
    pasteFromClipboard: "Beillesztés a vágólapról",
    readFailed: "A vágólap nem olvasható — illessze be alább.",
  },
  install: {
    title: "Reflecto telepítése",
    body: "Add a kezdőképernyődhöz a gyorsabb hozzáférésért.",
    cta: "Telepítés",
    dismissAria: "Telepítési kérés elvetése",
  },
  nav: {
    home: "Kezdőlap",
    clipboard: "Vágólap",
    files: "Fájlok",
    ariaLabel: "Fő navigáció",
    fileHiddenPublicMode: "Elrejtve, amíg a privát mód be van kapcsolva.",
  },
  common: {
    loading: "Betöltés",
  },
  errors: {
    notPaired: "Nincs párosítva",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} (${os})`,
    webFallback: "Webböngésző",
  },

  palette: {
    placeholder: "Írj egy parancsot…",
    noResults: "Nincs egyező parancs",
    ariaLabel: "Parancspaletta",
    clearAll: "Összes értesítés törlése",
    glance: "Glance-összefoglaló",
    cancelSnooze: "Szüneteltetés megszakítása",
    copyClip: "Legutóbb kapott klip másolása",
  },

  fileTransfer: {
    sendTitle: "Fájl küldése",
    sendChooseFile: "Fájl kiválasztása",
    sendDeviceLabel: "Küldés ide",
    sendPhoneFallbackName: "Telefon",
    send: "Fájl küldése",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Előkészítés…" : `Előkészítés — ${pct}%`,
    sendDelivering: "Kézbesítés a telefonodra…",
    sendWaitingAccept: "Várakozás, hogy a telefonod elfogadja…",
    sendWaitingAppOpen: "Nyisd meg a Reflectót a telefonodon az elfogadáshoz",
    sendWaitingAppOpenHint:
      "A telefonod nem tud push-értesítéseket fogadni, ezért csak akkor mutatja ezt a fájlt, ha a Reflecto meg van nyitva.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `${partNumber}/${partCount}. rész feltöltése…`,
    sendDone: "Elküldve.",
    sendFailedRejected: "A telefonod elutasította a fájlt.",
    sendFailedNoAnswer: "Nem érkezett válasz a telefonodtól.",
    sendFailedUploadFailed: "A feltöltés sikertelen. Próbáld újra.",
    sendFailedCancelled: "Megszakítva.",
    sendFailedGeneric: "Hiba történt. Próbáld újra.",
    sendTooLarge: (maxMiB: number): string => `A fájl túl nagy (legfeljebb ${maxMiB} MiB).`,
    sendKeepOpenHint: "Tartsd nyitva ezt a lapot, amíg az átvitel be nem fejeződik.",
    sendAnother: "Másik küldése",
    sendNoDevice: "Fájlok küldéséhez előbb párosíts egy telefont.",
    sendCancel: "Mégse",
    sendPeerProgress: (chunksDone: number): string =>
      `A telefon eddig ${chunksDone} darabot kapott meg.`,

    receiveTitle: "Bejövő fájlok",
    receiveOfferFrom: (sender: string): string => `${sender} fájlt szeretne küldeni neked`,
    receiveAccept: "Elfogadás",
    receiveDecline: "Elutasítás",
    receiveReceiving: (n: number, total: number): string => `${n}/${total}. rész fogadása…`,
    receiveDone: "Mentve.",
    receiveFailed: "Az átvitel sikertelen.",
    receiveCancelled: "Megszakítva.",
    receiveCancel: "Mégse",

    errorChunkAuthFail:
      "Az átvitel sikertelen — az adatokat nem sikerült ellenőrizni. Próbáld újra.",
    errorHashMismatch:
      "Az átvitel sikertelen — a fájl nem ment át az integritás-ellenőrzésen. Próbáld újra.",
    errorStorageFull: "Nincs elég tárhely ehhez az átvitelhez.",
    errorExpired: "Az átvitel lejárt.",
    errorQuotaExceeded: "Elérted a napi átviteli korlátot — próbáld újra holnap.",
    errorInternal: "Az átvitel sikertelen — valami hiba történt.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct}%`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Elvetés",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Átvitel állapota",
    outgoing: (n: number): string => `Kimenő (${n})`,
    incoming: (n: number): string => `Bejövő (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Összes elfogadása",
    dropTitle: "Húzza ide",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Legfeljebb ${size} méretű fájlok.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Húzza ide, vagy válasszon küldendő fájlt",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Közvetlen",
    transportDirectHint: "Eszközről eszközre küldve — soha nem tároljuk a szervereinken.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Szerveren át",
    transportRelayHint:
      "Szervereinken keresztül továbbítva, továbbra is végpontok közötti titkosítással — nem tudjuk elolvasni.",
    directBadge: "Közvetlen",
  },
  time: {
    justNow: "épp most",
    minutesAgo: (count: number): string => `${count} perce`,
    hoursAgo: (count: number): string => `${count} órája`,
  },
} satisfies MessagesShape;
